import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import MoreIcon16pxSvg from '../../../assets/images/icons/MoreIcon16pxSvg';
import {
    ClickAwayListener,
    Grow,
    MenuItem,
    Paper,
    Popper,
} from "@mui/material";
import { CustomAdvancedMenuList } from '../../../components/StyledComponents';
import ExportIcon16pxSvg from '../../../assets/images/icons/ExportIcon16pxSvg';
import DeleteIcon16pxSvg from '../../../assets/images/icons/DeleteIcon16pxSvg';
import EditIcon16pxSvg from '../../../assets/images/icons/EditIcon16pxSvg';
import { format, parse, parseISO } from 'date-fns';
import EditVisitorEventDialog from './EditVisitorEventDialog';
import { useLoading } from '../../../context/LoadingContext';
import { getEvents } from '../../../axios/Visitor/VisitorApi';
import DeleteEventDialog from './DeleteEventDialog';
import SendIconV216pxSvg from '../../../assets/images/icons/SendIconV216pxSvg';
import ShareVisitorEventDialog from './ShareVisitorEventDialog';
import CustomPagination from '../CustomPagination';
import NoRowOverlay from '../../../assets/images/icons/NoRowOverlay';

const RosterCard = ({ event, selected, onSelect, onEdit, onDelete, openShare }) => {
    const { t } = useTranslation();
    const { isEmployee, isAdmin, genaralSettingDateFormat, genaralSettingTimeFormat } = useAuth();
    const [openMenu, setOpenMenu] = useState(false);
    const anchorRef = useRef(null);


    const formatTime = (time, formatStr) => {
        if (!time) return "-";
        try {
            const parsed = parse(time, "HH:mm:ss", new Date());
            return format(parsed, formatStr);
        } catch {
            return time;
        }
    };

    const handleToggle = (e) => {
        e.stopPropagation();
        setOpenMenu((prev) => !prev);
    };

    const handleClose = (e) => {
        e?.stopPropagation();
        setOpenMenu(false);
    };

    return (
        <div
            onClick={onSelect}
            className={`
        group
        relative rounded-xl border p-4 cursor-pointer transition 
        ${selected
                    ? "border-[#0057FF] bg-[#F5F8FF]"
                    : "border-[#E5E7EB] hover:border-[#0057FF] hover:bg-[#F5F8FF]"
                }
      `}
        >
            <div className="flex justify-between items-start gap-3">
                <div>
                    <p
                        className={`
    font-semibold text-base transition-colors
    ${selected ? "text-[#0057FF]" : "text-[#1F2937] group-hover:text-[#0057FF]"}
  `}
                    >
                        {event.name}
                    </p>

                    <div className="mt-2 text-sm text-gray-600 space-y-2">
                        {/* DATE */}
                        <div className="grid grid-cols-2 gap-3">
                            <span className="font-medium text-[#6B7280]">
                                {t("common.date")}
                            </span>

                            <span className="text-[#111827]">
                                {format(parseISO(event.startDate), genaralSettingDateFormat)} -{" "}
                                {format(parseISO(event.endDate), genaralSettingDateFormat)}
                            </span>
                        </div>

                        {/* TIME */}
                        <div className="grid grid-cols-2 gap-3">
                            <span className="font-medium text-[#6B7280]">
                                {t("common.content-036")}
                            </span>

                            <span className="text-[#111827]">
                                {genaralSettingTimeFormat
                                    ? formatTime(event.startTime, genaralSettingTimeFormat)
                                    : event.startTime}
                                {" - "}
                                {genaralSettingTimeFormat
                                    ? formatTime(event.endTime, genaralSettingTimeFormat)
                                    : event.endTime}
                            </span>
                        </div>

                        {/* PARTICIPANTS */}
                        <div className="grid grid-cols-2 gap-3">
                            <span className="font-medium text-[#6B7280]">
                                {t("common.participants-count")}
                            </span>

                            <span className="text-[#111827]">
                                {event.maxParticipants}
                            </span>
                        </div>
                    </div>
                </div>

                {isAdmin && (
                    <div onClick={(e) => e.stopPropagation()}>
                        {/* 3 dots menu */}
                        <div
                            ref={anchorRef}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleToggle(e);
                            }}
                            className="
    group
    p-1
    rounded-full
    cursor-pointer
    text-[#1F2937]
    hover:text-[#0057FF]
    transition
  "
                        >
                            <MoreIcon16pxSvg width={16} height={16} />
                        </div>

                        <Popper
                            open={openMenu}
                            anchorEl={anchorRef.current}
                            placement="bottom-end"
                            transition
                            zIndex={99999}
                        >
                            {({ TransitionProps }) => (
                                <Grow {...TransitionProps}>
                                    <Paper elevation={3}>
                                        <ClickAwayListener onClickAway={handleClose}>
                                            <CustomAdvancedMenuList autoFocusItem={openMenu}>
                                                <MenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleClose();
                                                        openShare();
                                                    }}
                                                    className="!text-[#1F2937]"
                                                    disabled={!isAdmin || isEmployee}
                                                >
                                                    <SendIconV216pxSvg
                                                        width={16}
                                                        height={16}
                                                        color="#1F2937"
                                                        className="!mr-2"
                                                    />
                                                    {t("common.share")}
                                                </MenuItem>

                                                <MenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleClose();
                                                        onEdit();
                                                    }}
                                                    className="!text-[#1F2937]"
                                                    disabled={!isAdmin || isEmployee}
                                                >
                                                    <EditIcon16pxSvg
                                                        width={16}
                                                        height={16}
                                                        color="#1F2937"
                                                        className="mr-2"
                                                    />
                                                    {t("button.edit")}
                                                </MenuItem>

                                                <MenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleClose();
                                                        onDelete();
                                                    }}
                                                    className="!text-[#D00D00]"
                                                    disabled={!isAdmin || isEmployee}
                                                >
                                                    <DeleteIcon16pxSvg
                                                        width={16}
                                                        height={16}
                                                        color="#D00D00"
                                                        className="mr-2"
                                                    />
                                                    {t("button.delete")}
                                                </MenuItem>

                                                <MenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleClose();
                                                    }}
                                                    className="!text-[#1F2937]"
                                                    disabled
                                                >
                                                    <ExportIcon16pxSvg
                                                        width={16}
                                                        height={16}
                                                        color="#1F2937"
                                                        className="mr-2"
                                                    />
                                                    {t("button.export-data")}
                                                </MenuItem>
                                            </CustomAdvancedMenuList>
                                        </ClickAwayListener>
                                    </Paper>
                                </Grow>
                            )}
                        </Popper>
                    </div>
                )}
                {/* 3 dots menu */}
            </div>
        </div>
    );
};

const VisitorRoster = ({ usersData, onEdit, data, refreshData, onRefresh, onRowSelection, searchText, onPaginationChange }) => {
    const { t } = useTranslation();
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectedRoster, setSelectedRoster] = useState(null);
    const [openEditRoster, setOpenEditRoster] = useState(false);
    const [openDeleteRoster, setOpenDeleteRoster] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [show, hide] = useLoading();
    const [totalElements, setTotalElements] = useState(0);
    const [openShare, setOpenShare] = useState(false);

    useEffect(() => {
        fetchEvents();
    }, [page, pageSize, refreshData, searchText]);

    const fetchEvents = async () => {
        const payload = {
            pageNo: page,
            recordPerPage: pageSize,
            searchText,
        };
        try {
            show();
            const response = await getEvents(payload);

            setRows(response?.data?.data || []);

            const total = response?.data?.pagination?.totalPages || 1;

            setTotalPages(total);

            setTotalElements(
                response?.data?.pagination?.totalElements || 0
            );

            // ← thêm block này
            onPaginationChange?.({
                totalPages: total,
                pageSize,
            });

        } catch (error) {
            console.error(error);
        } finally {
            hide();
        }
    };

    const onSelectRoster = (row) => {
        setSelectedRowId(row.id);
        onRowSelection(row);
    };

    const handleEditById = (eventId) => {
        const found = rows.find(item => item.id === eventId);

        if (!found) {
            console.error("Không tìm thấy event");
            return;
        }

        setSelectedRoster(found);
        setOpenEditRoster(true);
    };

    const handleRowsPerPageChange = (newPageSize) => {
        const size =
            typeof newPageSize === "number"
                ? newPageSize
                : parseInt(newPageSize.target?.value ?? newPageSize, 10);

        setPageSize(size);
        setPage(1);
    };

    return (
        <>
            <div className="rosters">
                <div className="min-h-[59vh] flex flex-col">
                    {rows.length === 0 ? (
                        <div className="flex-1 flex items-center justify-center">
                            <NoRowOverlay />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-6">
                            <div className="flex flex-col grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{rows.map((event) => (
                                <RosterCard
                                    key={event.id}
                                    event={event}
                                    selected={event.id === selectedRowId}
                                    onSelect={() => onSelectRoster(event)}
                                    onEdit={() => handleEditById(event.id)}
                                    openShare={() => {
                                        setSelectedRoster(event);
                                        setOpenShare(true);
                                    }}
                                    onDelete={() => {
                                        setSelectedRoster(event);
                                        setOpenDeleteRoster(true);
                                    }}
                                />
                            ))}</div>



                        </div>
                    )}


                </div>

                <div className="mt-6">
                    <CustomPagination
                        page={page - 1}
                        count={totalElements}
                        rowsPerPage={pageSize}
                        onPageChange={(newPage) => setPage(newPage + 1)}
                        onRowsPerPageChange={handleRowsPerPageChange}
                    />
                </div>
            </div>

            <EditVisitorEventDialog
                open={openEditRoster}
                handleClose={() => {
                    setOpenEditRoster(false);
                    setSelectedRoster(null);
                }}
                usersData={usersData}
                initialData={selectedRoster}
                onRefresh={onRefresh}
                id={selectedRoster?.id}
            />

            <DeleteEventDialog
                open={openDeleteRoster}
                onClose={() => {
                    setOpenDeleteRoster(false);
                    setSelectedRoster(null);
                }}
                data={selectedRoster}
                onRefresh={onRefresh}
                id={selectedRoster?.id}
            />

            <ShareVisitorEventDialog
                open={openShare}
                handleClose={() => {
                    setOpenShare(false);
                    setSelectedRoster(null);
                }}
                onRefresh={onRefresh}
                id={selectedRoster?.id}
                dataInfo={selectedRoster}
                refreshData={refreshData}
            />
        </>
    )
}

export default VisitorRoster